import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, ConfigProvider, Drawer, Grid, Layout, theme } from 'antd'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import ContextCompanySelect from '../../features/contextFilters/components/ContextCompanySelect'
import { contextCompanyIdSelector } from '../../redux/context/company/selectors'
import UserTour from '../../components/Tour/Tour'
import TermsAndConditions from '../../components/TermsConditions/TermsConditions'
import { useAuthorizedData } from '../../utils/Authorizable/authorize'
import BoundaryFallback from '../../components/Error/BoundaryFallback'
import CompanyContextManager from '../ContextManager'
import Loading from '../../shared/components/Loading'
import EmptyState from '../../shared/components/EmptyState'
import LucideIcon from '../../shared/components/LucideIcon'
import { Route } from '../../routes/Route'
import AppHeader, { HEADER_HEIGHT } from './AppHeader'
import AppLogo from './AppLogo'
import SettingsNavHeader from './SettingsNavHeader'
import SideNav from './SideNav'
import { SETTINGS_ROOT, isSubPath, useNavigationMode, useReturnPath } from './useNavigationMode'
import { PageContainerProvider } from './PageContainerContext'

const SIDER_WIDTH = 224
const SIDER_COLLAPSED_WIDTH = 64

const SiderColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const SiderMenu = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

const SiderLogo = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  padding-inline: ${({ $collapsed }) => ($collapsed ? 8 : 16)}px;
`

/** Routes that work without a selected company. */
const contextRoutes = [
  '/settings/user',
  '/settings/accountant',
  '/settings/financier',
  '/dashboard/accountant',
  '/dashboard/financier',
  '/deliveries'
]

interface AppShellProps {
  route?: Route
}

const AppShell: React.FC<AppShellProps> = ({ route }) => {
  const location = useLocation()
  const companyId = useSelector(contextCompanyIdSelector)
  const { t, i18n } = useTranslation()
  const authorizedRoute = useAuthorizedData(route)
  const menuRoutes = authorizedRoute?.routes ?? []

  const { mode } = useNavigationMode()
  const returnPath = useReturnPath(mode)
  const settingsContexts = menuRoutes.find(r => r.path === SETTINGS_ROOT)?.routes ?? []
  // Longest match, so e.g. /settings/dashboards/company/order resolves to its own settings section
  const activeSettingsRoute = settingsContexts
    .filter(r => isSubPath(location.pathname, r.path))
    .sort((a, b) => b.path.length - a.path.length)[0]
  const navRoutes = mode === 'settings' ? activeSettingsRoute?.routes ?? [] : menuRoutes

  const {
    token: { colorBgContainer, colorPrimary, colorBorder, borderRadiusLG, boxShadowTertiary }
  } = theme.useToken()

  const screens = Grid.useBreakpoint()
  // useBreakpoint returns {} before the first measurement, so only treat an explicit false as mobile.
  const isMobile = screens.md === false

  const [collapsed, setCollapsed] = useState(false)
  const [pageContainerCount, setPageContainerCount] = useState(0)

  useEffect(() => {
    if (isMobile) setCollapsed(true)
  }, [isMobile])

  useEffect(() => {
    document.title = `Finadeck - ${t(`menu:/${location.pathname.split('/')[1]}`, 'App')}`
  }, [location.pathname, i18n.language])

  const registerPageContainer = useCallback(() => {
    setPageContainerCount(count => count + 1)
    return () => setPageContainerCount(count => count - 1)
  }, [])

  const pageContainerContext = useMemo(
    () => ({ register: registerPageContainer, routes: menuRoutes, windowScroll: isMobile }),
    [registerPageContainer, menuRoutes, isMobile]
  )

  const toggleCollapsed = () => setCollapsed(!collapsed)

  const renderNavigation = (navCollapsed: boolean, onNavigate?: () => void) => (
    <>
      {mode === 'settings' && (
        <SettingsNavHeader
          contexts={settingsContexts}
          activeContextPath={activeSettingsRoute?.path}
          returnPath={returnPath}
          collapsed={navCollapsed}
          onNavigate={onNavigate}
        />
      )}
      <SideNav routes={navRoutes} collapsed={navCollapsed} onNavigate={onNavigate} />
    </>
  )

  // The frame around the content panel is tinted with the theme's primary colour, so white-label themes get
  // their own tint. Mixed by hand because antd's lightest shade (colorPrimaryBg) is too strong here.
  const frameBg = `color-mix(in srgb, ${colorPrimary} 5%, ${colorBgContainer})`

  return (
    <ConfigProvider theme={{ components: { Layout: { bodyBg: frameBg, headerBg: frameBg, lightSiderBg: frameBg } } }}>
      {/* Wider screens fix the frame to the viewport and scroll inside the content panel instead */}
      <Layout hasSider style={isMobile ? { minHeight: '100vh' } : { height: '100vh', overflow: 'hidden' }}>
        {isMobile ? (
          <Drawer
            placement="left"
            open={!collapsed}
            onClose={() => setCollapsed(true)}
            closable={false}
            size={SIDER_WIDTH}
            styles={{ body: { padding: 0, background: frameBg } }}
          >
            {renderNavigation(false, () => setCollapsed(true))}
          </Drawer>
        ) : (
          <Layout.Sider
            theme="light"
            width={SIDER_WIDTH}
            collapsedWidth={SIDER_COLLAPSED_WIDTH}
            collapsed={collapsed}
            trigger={null}
            breakpoint="lg"
            onBreakpoint={setCollapsed}
            style={{
              position: 'sticky',
              top: 0,
              height: '100vh'
            }}
          >
            <SiderColumn>
              <SiderLogo $collapsed={collapsed} style={{ height: HEADER_HEIGHT }}>
                <AppLogo collapsed={collapsed} />
              </SiderLogo>
              <SiderMenu>{renderNavigation(collapsed)}</SiderMenu>
              <div style={{ padding: 4 }}>
                <Button
                  className="side-nav-collapse"
                  type="text"
                  block
                  onClick={toggleCollapsed}
                  icon={<LucideIcon icon={collapsed ? PanelLeftOpen : PanelLeftClose} size={16} />}
                  title={collapsed ? t('global:expand-menu', 'Laajenna valikko') : undefined}
                  style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
                >
                  {!collapsed && t('global:collapse-menu', 'Pienennä valikko')}
                </Button>
              </div>
            </SiderColumn>
          </Layout.Sider>
        )}
        <Layout style={{ minWidth: 0 }}>
          <AppHeader onOpenMenu={isMobile ? () => setCollapsed(false) : undefined} />
          <Layout.Content
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              minHeight: 0,
              // Pages scroll inside the panel. A page container fills the panel and scrolls its own content
              // area, so this only scrolls pages without one.
              overflow: isMobile ? undefined : 'auto',
              // Content sits in a raised panel inset into the app frame.
              margin: isMobile ? '0 8px 8px' : '0 10px 10px 0',
              background: colorBgContainer,
              border: `1px solid ${colorBorder}`,
              borderRadius: borderRadiusLG,
              boxShadow: boxShadowTertiary,
              // Pages with their own page container handle padding themselves.
              padding: pageContainerCount > 0 ? 0 : '32px 40px'
            }}
          >
            <PageContainerProvider value={pageContainerContext}>
              {companyId ||
              location.pathname === SETTINGS_ROOT ||
              contextRoutes.some(r => location.pathname.startsWith(r)) ? (
                <ErrorBoundary FallbackComponent={BoundaryFallback}>
                  {/* Route pages load lazily: keep the shell visible while a page chunk loads */}
                  <Suspense fallback={<Loading />}>
                    <CompanyContextManager />
                  </Suspense>
                </ErrorBoundary>
              ) : (
                <EmptyState
                  illustration="selectCompany"
                  title={t('global:set-context-company-text')}
                  action={<ContextCompanySelect />}
                />
              )}
            </PageContainerProvider>
            <TermsAndConditions />
            <UserTour />
          </Layout.Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  )
}

export default AppShell
